import { clsx } from "clsx";
import type { AnimationItem, LottiePlayer } from "lottie-web";
import { memo, useCallback, useEffect, useRef } from "react";
import type { CSSProperties, HTMLAttributes } from "react";
import { animatedIconLoaders } from "./animatedIcons";
import type { AnimatedIconName } from "./animatedIcons";
import { prepareAnimation } from "./prepareAnimation";

export { animatedIconNames } from "./animatedIcons";
export type { AnimatedIconName } from "./animatedIcons";

let playerModule: Promise<LottiePlayer> | undefined;
// The light SVG player has no After Effects expression engine. The full player
// evaluates the exports' expressions with eval(), which the production CSP
// (script-src without 'unsafe-eval') refuses; here they fall back to keyframes.
const loadPlayer = () =>
  (playerModule ??= import("lottie-web/build/player/lottie_light").then(
    // CommonJS compilation turns import() into require(), with no default wrapper.
    (module) => module.default ?? (module as unknown as LottiePlayer)
  ));

export type AnimatedProps = Omit<
  HTMLAttributes<HTMLSpanElement>,
  "children"
> & {
  /** The export's filename, without .json. */
  name: AnimatedIconName;
  /** A $colors name from _variables.scss, or any CSS color. Inherits by default. */
  color?: string;
  /** Settle on the finished frame; setting false resumes playback. */
  paused?: boolean;
  /** Repeat the animation. Set false to play once. */
  loop?: boolean;
  /** Width and height; defaults to 1.5rem, also adjustable with className/style. */
  size?: CSSProperties["width"];
};

export const Animated = memo(function Animated({
  name,
  color,
  paused = false,
  loop = true,
  size,
  className,
  style,
  ...rest
}: AnimatedProps) {
  const container = useRef<HTMLSpanElement>(null);
  const animation = useRef<AnimationItem>();
  const controls = useRef({ paused, loop });
  const reducedMotion = useRef(false);

  // Settle on the last frame rather than pausing in place: these exports build
  // their subject up over time, so early frames are blank or half-drawn.
  const syncPlayback = useCallback(() => {
    const player = animation.current;
    if (!player) return;
    player.loop = controls.current.loop;
    if (controls.current.paused || reducedMotion.current)
      player.goToAndStop(Math.max(player.totalFrames - 1, 0), true);
    else player.play();
  }, []);

  useEffect(() => {
    controls.current = { paused, loop };
    syncPlayback();
  }, [paused, loop, syncPlayback]);

  useEffect(() => {
    const element = container.current;
    if (!element) return;

    let disposed = false;
    let player: AnimationItem | undefined;
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    reducedMotion.current = media?.matches ?? false;

    const onMotionChange = () => {
      reducedMotion.current = media?.matches ?? false;
      syncPlayback();
    };
    media?.addEventListener("change", onMotionChange);

    // Import in the effect so the browser-only player is safe for SSR and is
    // downloaded only when an animated icon is actually mounted.
    void Promise.all([loadPlayer(), animatedIconLoaders[name]()])
      .then(([lottie, dataModule]) => {
        if (disposed) return;
        player = lottie.loadAnimation({
          container: element,
          renderer: "svg",
          animationData: prepareAnimation(dataModule.default ?? dataModule),
          autoplay: false,
          loop: controls.current.loop,
          rendererSettings: {
            preserveAspectRatio: "xMidYMid meet",
            focusable: false,
          },
        });
        animation.current = player;
        // Frames only render once assets finish loading, so sync again then.
        player.addEventListener("DOMLoaded", syncPlayback);
        syncPlayback();
      })
      .catch((error: unknown) => {
        if (!disposed)
          console.error(`Unable to load animated icon "${name}".`, error);
      });

    return () => {
      disposed = true;
      media?.removeEventListener("change", onMotionChange);
      player?.destroy();
      animation.current = undefined;
    };
  }, [name, syncPlayback]);

  // Sass publishes the palette as CSS variables, keeping it the source of truth.
  const resolvedColor =
    color && /^[a-z][a-z0-9]*$/i.test(color)
      ? `var(--rujira-color-${color}, ${color})`
      : color;
  const hasLabel = Boolean(rest["aria-label"] || rest["aria-labelledby"]);

  return (
    <span
      role={hasLabel ? "img" : undefined}
      aria-hidden={hasLabel ? undefined : true}
      {...rest}
      ref={container}
      className={clsx("rujira-animated", className)}
      style={{
        width: size,
        height: size,
        ...style,
        ...(color ? { color: resolvedColor } : {}),
      }}
    />
  );
});
